import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getCreditsBalance } from "../api/creditApi";
import {
  buildCreditBalanceViewModel,
  getNextTrialDayBoundaryDelay,
  getTrialProfileMetadata,
} from "../helper/creditBalanceModel";
import { getDevCreditsBalancePayload } from "../helper/devCreditsBalance";
import { subscribeToCreditState } from "../helper/creditStateEvents";

const CreditsBalanceContext = createContext({
  balance: null,
  rawBalance: null,
  loading: false,
  refreshing: false,
  error: null,
  refusalDetail: null,
  providerPresent: false,
  refreshBalance: async () => {},
});

export function useCreditsBalance() {
  return useContext(CreditsBalanceContext);
}

export function CreditsBalanceProvider({
  children,
  enabled = true,
  isV2TableVisual = true,
  profile = null,
}) {
  const [rawBalance, setRawBalance] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [refusalDetail, setRefusalDetail] = useState(null);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const sequenceRef = useRef(0);
  const rawBalanceRef = useRef(null);

  useEffect(() => {
    rawBalanceRef.current = rawBalance;
  }, [rawBalance]);

  const refreshBalance = useCallback(async () => {
    if (!enabled) return null;

    const sequence = ++sequenceRef.current;
    if (rawBalanceRef.current === null) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    try {
      const response = await getCreditsBalance();
      if (sequence !== sequenceRef.current) return null;

      if (response?.status === 200) {
        setRawBalance(response.data);
        setError(null);
        return response.data;
      }

      const devFallback = getDevCreditsBalancePayload({ isV2TableVisual });
      if (devFallback) {
        setRawBalance(devFallback);
        setError(null);
        return devFallback;
      }

      setError("Credits unavailable");
      return null;
    } catch (_err) {
      if (sequence !== sequenceRef.current) return null;
      const devFallback = getDevCreditsBalancePayload({ isV2TableVisual });
      if (devFallback) {
        setRawBalance(devFallback);
        setError(null);
        return devFallback;
      }
      setError("Credits unavailable");
      return null;
    } finally {
      if (sequence === sequenceRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [enabled, isV2TableVisual]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    refreshBalance();
    return undefined;
  }, [enabled, refreshBalance]);

  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeToCreditState((event) => {
      if (event.type === "metered-operation-refused") {
        setRefusalDetail(event.refusalDetail);
      } else if (event.clearRefusal) {
        setRefusalDetail(null);
      }
      void refreshBalance();
    });
  }, [enabled, refreshBalance]);

  useEffect(() => {
    setRefusalDetail(null);
  }, [profile]);

  const { trialExpiresAt } = getTrialProfileMetadata(profile);
  useEffect(() => {
    const delay = getNextTrialDayBoundaryDelay(trialExpiresAt, clockNow);
    if (delay === null) return undefined;
    const timer = setTimeout(() => setClockNow(Date.now()), delay);
    return () => clearTimeout(timer);
  }, [clockNow, trialExpiresAt]);

  const balance = useMemo(
    () => (
      rawBalance || trialExpiresAt
        ? buildCreditBalanceViewModel(rawBalance || {}, { profile, now: clockNow })
        : null
    ),
    [clockNow, profile, rawBalance, trialExpiresAt]
  );

  const value = useMemo(
    () => ({
      balance,
      rawBalance,
      loading,
      refreshing,
      error,
      refusalDetail,
      providerPresent: true,
      refreshBalance,
    }),
    [balance, error, loading, refreshing, rawBalance, refusalDetail, refreshBalance]
  );

  return (
    <CreditsBalanceContext.Provider value={value}>
      {children}
    </CreditsBalanceContext.Provider>
  );
}

export default CreditsBalanceContext;
